console.log('Pre-order form script loaded');

class PreOrderForm {
  constructor() {
    // Only select the actual form containers (not the nested form divs)
    this.forms = document.querySelectorAll('[id^="pre-order-form-container-"][id$="__main"]');
    this.variantChangeUnsubscriber = undefined;
    this.currentVariantId = null;
    this.currentProductData = null;
    this.initialized = false;
    this.init();
  }

  init() {
    if (this.initialized) {
      return;
    }
    
    console.log('Initializing pre-order form, found containers:', this.forms.length);
    
    this.forms.forEach((form, index) => {
      this.setupForm(form);
      this.ensureInitialButtonState(form);
    });
    
    // Listen for variant changes
    this.variantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.variantChange,
      this.handleVariantChange.bind(this)
    );
    
    // Get initial variant data
    this.updateCurrentVariantData();
    
    this.initialized = true;
    console.log('Pre-order form initialization complete');
  }

  ensureInitialButtonState(form) {
    const buttonText = form.querySelector('.pre-order-form__button-text');
    const buttonLoading = form.querySelector('.pre-order-form__button-loading');
    const submitButton = form.querySelector('button[type="button"]');
    
    if (buttonText && buttonLoading && submitButton) {
      // Force the correct state with both hidden attribute and style
      buttonText.hidden = false;
      buttonText.style.display = '';
      buttonText.style.visibility = '';
      
      buttonLoading.hidden = true;
      buttonLoading.style.display = 'none';
      buttonLoading.style.visibility = 'hidden';
      
      submitButton.disabled = false;
    }
  }

  handleVariantChange(event) {
    const { data } = event;
    if (data && data.variant) {
      this.currentVariantId = data.variant.id;
      this.currentProductData = {
        productId: data.variant.product_id,
        productTitle: document.querySelector('h1')?.textContent?.trim() || 'Product',
        variantId: data.variant.id
      };
      
      console.log('Pre-order form: Variant changed to:', {
        variantId: this.currentVariantId,
        productId: this.currentProductData.productId,
        productTitle: this.currentProductData.productTitle
      });
      
      // Update any visible forms to show current variant info
      this.updateFormVariantInfo();
    }
  }

  updateFormVariantInfo() {
    // This method can be used to update form UI if needed
    // For now, we'll just log the current state
    console.log('Pre-order form ready for variant:', this.currentVariantId);
  }

  updateCurrentVariantData() {
    // Try to get current variant from various sources
    const variantInput = document.querySelector('input[name="id"]');
    const variantSelect = document.querySelector('select[name="id"]');
    
    if (variantInput && variantInput.value) {
      this.currentVariantId = parseInt(variantInput.value);
    } else if (variantSelect && variantSelect.value) {
      this.currentVariantId = parseInt(variantSelect.value);
    }
    
    this.currentProductData = this.getProductData();
  }

  setupForm(form) {
    const submitButton = form.querySelector('button[type="button"]');
    const emailInput = form.querySelector('input[type="email"]');
    const errorElement = form.querySelector('[role="alert"]');
    const statusElement = form.querySelector('[role="status"]');
    const buttonText = form.querySelector('.pre-order-form__button-text');
    const buttonLoading = form.querySelector('.pre-order-form__button-loading');
    
    // Ensure initial state is correct
    if (buttonText) buttonText.hidden = false;
    if (buttonLoading) buttonLoading.hidden = true;

    submitButton.addEventListener('click', async (e) => {
      e.preventDefault();
      
      console.log('=== PRE-ORDER FORM SUBMISSION STARTED ===');
      console.log('Button clicked, starting submission process...');
      
      const email = emailInput.value.trim();
      console.log('Email input:', email);
      
      if (!this.validateEmail(email)) {
        console.log('Email validation failed:', { email, isValid: this.validateEmail(email) });
        this.showError(errorElement, this.getTranslation('products.pre_order.error'));
        return;
      }

      console.log('Email validation passed');
      console.log('Setting loading state to true');
      this.setLoadingState(submitButton, buttonText, buttonLoading, true);
      this.hideError(errorElement);
      this.hideStatus(statusElement);

      try {
        console.log('Calling submitToKlaviyo...');
        await this.submitToKlaviyo(email, form);
        console.log('Klaviyo submission successful!');
        this.showSuccess(statusElement, this.getTranslation('products.pre_order.success'));
        emailInput.value = ''; // Clear the form
      } catch (error) {
        console.error('=== PRE-ORDER SUBMISSION ERROR ===');
        console.error('Error type:', error.constructor.name);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        console.error('Full error object:', error);
        this.showError(errorElement, 'Something went wrong. Please try again.');
      } finally {
        console.log('Setting loading state to false');
        this.setLoadingState(submitButton, buttonText, buttonLoading, false);
        console.log('=== PRE-ORDER SUBMISSION COMPLETED ===');
      }
    });

    emailInput.addEventListener('input', () => {
      this.hideError(errorElement);
      this.hideStatus(statusElement);
    });
  }

  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  async submitToKlaviyo(email, form) {
    console.log('=== SUBMIT TO KLAVIYO STARTED ===');
    console.log('Email:', email);
    console.log('Form ID:', form.id);
    
    // The form IS the container, so we don't need to look it up
    const container = form;
    console.log('Using form as container:', container.id);

    // Use current variant data if available, otherwise fall back to getProductData
    const productData = this.currentProductData || this.getProductData();
    console.log('Product data:', productData);
    
    if (!productData.variantId) {
      console.error('Product variant ID not found in data:', productData);
      throw new Error('Product variant ID not found');
    }

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email
              }
            }
          },
          channels: ['EMAIL']
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${productData.variantId}`
            }
          }
        }
      }
    };

    console.log('Payload to send:', JSON.stringify(payload, null, 2));

    try {
      console.log('Making fetch request to Klaviyo...');
      const response = await fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=RrQGjG', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2025-07-15'
        },
        body: JSON.stringify(payload)
      });

      console.log('Response received:', {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        console.error('=== KLAVIYO API ERROR ===');
        console.error('Response status:', response.status);
        console.error('Response statusText:', response.statusText);
        
        try {
          const errorData = await response.json();
          console.error('Klaviyo API error response:', errorData);
          console.error('Error details:', JSON.stringify(errorData, null, 2));
          throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
        } catch (parseError) {
          console.error('Could not parse error response:', parseError);
          const errorText = await response.text();
          console.error('Raw error response:', errorText);
          throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
        }
      }

      // Handle empty response (202 status with no content)
      if (response.status === 202 || response.headers.get('content-length') === '0') {
        console.log('Klaviyo subscription created successfully (202 - Accepted)');
        console.log('=== SUBMIT TO KLAVIYO SUCCESS ===');
        return { success: true, status: response.status };
      }

      const responseData = await response.json();
      console.log('Klaviyo response data:', responseData);
      console.log('=== SUBMIT TO KLAVIYO SUCCESS ===');
      return responseData;
      
    } catch (error) {
      console.error('=== SUBMIT TO KLAVIYO ERROR ===');
      console.error('Fetch error type:', error.constructor.name);
      console.error('Fetch error message:', error.message);
      console.error('Fetch error stack:', error.stack);
      
      // Check if it's a network error
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('Network error detected - check internet connection and Klaviyo endpoint');
      }
      
      // Check if it's a CORS error
      if (error.message.includes('CORS')) {
        console.error('CORS error detected - check Klaviyo CORS settings');
      }
      
      console.error('Full error object:', error);
      throw error;
    }
  }

  getProductData() {
    const productData = window.Shopify?.analytics?.meta?.product;
    
    if (productData) {
      return {
        productId: productData.id,
        productTitle: productData.title,
        variantId: productData.selected_or_first_available_variant?.id
      };
    }

    const metaProduct = document.querySelector('meta[property="product:retailer_item_id"]');
    const metaVariant = document.querySelector('meta[property="product:price:amount"]');
    
    if (metaProduct && metaVariant) {
      return {
        productId: metaProduct.content,
        productTitle: document.querySelector('h1')?.textContent?.trim() || 'Product',
        variantId: this.extractVariantIdFromPage()
      };
    }

    return {
      productId: null,
      productTitle: document.querySelector('h1')?.textContent?.trim() || 'Product',
      variantId: this.extractVariantIdFromPage()
    };
  }

  extractVariantIdFromPage() {
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput) {
      return parseInt(variantInput.value);
    }

    const variantSelect = document.querySelector('select[name="id"]');
    if (variantSelect) {
      return parseInt(variantSelect.value);
    }

    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const hiddenInput = productForm.querySelector('input[name="id"]');
      if (hiddenInput) {
        return parseInt(hiddenInput.value);
      }
    }

    return null;
  }

  setLoadingState(button, buttonText, buttonLoading, isLoading) {
    if (!button || !buttonText || !buttonLoading) {
      return;
    }
    
    if (isLoading) {
      button.disabled = true;
      buttonText.hidden = true;
      buttonLoading.hidden = false;
    } else {
      button.disabled = false;
      buttonText.hidden = false;
      buttonLoading.hidden = true;
    }
  }

  showError(errorElement, message) {
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.hidden = false;
    }
  }

  hideError(errorElement) {
    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }
  }

  showSuccess(statusElement, message) {
    if (statusElement) {
      // Hide the form elements
      const form = statusElement.closest('.pre-order-form__form');
      if (form) {
        const emailField = form.querySelector('.pre-order-form__field');
        const submitButton = form.querySelector('button[type="button"]');
        
        if (emailField) emailField.classList.add('hidden');
        if (submitButton) submitButton.classList.add('hidden');
      }
      
      // Show the success message
      statusElement.textContent = message;
      statusElement.classList.remove('hidden');
      statusElement.className = 'pre-order-form__status pre-order-form__status--success';
    }
  }

  hideStatus(statusElement) {
    if (statusElement) {
      statusElement.hidden = true;
      statusElement.textContent = '';
      statusElement.className = 'pre-order-form__status';
    }
  }

  getTranslation(key) {
    const translations = {
      'products.pre_order.error': 'Please enter a valid email address.',
      'products.pre_order.success': 'Thank you! We\'ll notify you when this item is back in stock.'
    };
    return translations[key] || key;
  }

  destroy() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
  }
}

// Store global instance for cleanup
let preOrderFormInstance = null;
let domContentLoadedFired = false;

document.addEventListener('DOMContentLoaded', () => {
  if (domContentLoadedFired) {
    return;
  }
  domContentLoadedFired = true;
  
  const preOrderElements = document.querySelectorAll('[id^="pre-order-form-container-"]');
  
  if (preOrderElements.length > 0) {
    // Small delay to ensure DOM is fully ready
    setTimeout(() => {
      if (!preOrderFormInstance) {
        preOrderFormInstance = new PreOrderForm();
      }
    }, 100);
  }
});

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  if (preOrderFormInstance) {
    preOrderFormInstance.destroy();
  }
});
